import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  CartLineInput,
  CartLineUpdateInput,
  CustomAttributeInput,
  FigsCart,
} from '../../../__generated__/graphql/catalog/graphql'
import { cookieKeys } from '../../../app/_config/Cookies.config'
import { useCheckoutClient } from '../../../app/_providers/CheckoutClientProvider.client'
import { useCookies } from '../../../app/_providers/CookiesProvider.client'
import { useLocalization } from '../../../app/_providers/LocalizationProvider.client'
import { QueryStatus } from '../../hooks/types'

export interface CartStoreLineItem {
  cartLineId: string
  sku: string
  quantity: number
  attributes: Array<{ key: string; value: string }>
  discounts: Array<{
    amount: number // amount discounted
    currencyCode: string
    message?: string
  }>
  cost: {
    currencyCode: string
    amountPerQuantity: number // the amount of the merchandise line
    subtotalAmount: number // the cost of the merchandise line before line-level discounts
    totalAmount: number // total cost of the merchandise line
    compareAtAmountPerQuantity?: number // compare at amount of the merchandise line
  }
}

export interface CartStoreCart {
  id: string | null // allow for null in the event we haven't made a real cart yet
  lines: CartStoreLineItem[]
  totalQuantity: number
  cost: {
    amount: number
    currencyCode: string
  }
  discounts: Array<{
    amount: number
    currencyCode: string
    message?: string
  }>
  attributes: Array<{ key: string; value: string }>
}

export function useCartStore() {
  // TODO: we need to skip some degree of persistence if we have a gifting cart
  // TODO: pull CheckoutClient logic into this file. We don't need to keep separating concerns.
  const checkoutClient = useCheckoutClient()
  const { currency } = useLocalization() // regional currency to use as a fallback
  const [cookies, setCookie] = useCookies([cookieKeys.cartId.key])
  const cartIdCookie = cookies[cookieKeys.cartId.key]
  const [status, setStatus] = useState<Exclude<QueryStatus, 'idle'>>('pending')
  const [shopifyCart, setShopifyCart] = useState<Omit<FigsCart, 'checkoutUrl'>>()
  const [activeCartId, setActiveCartId] = useState<string>()

  // ensure cookie value is updated if the "active" `cartId` changes
  useEffect(() => {
    if (activeCartId && activeCartId !== cartIdCookie) {
      setCookie(cookieKeys.cartId.key, activeCartId, cookieKeys.cartId.options)
    }
  }, [activeCartId, cartIdCookie, setCookie])

  const cart = useMemo<CartStoreCart>(() => {
    // convert `shopifyCart` to preferred cart structure.
    if (shopifyCart) {
      return {
        id: shopifyCart.id!,
        lines:
          shopifyCart.lines?.edges
            .map(e => e.node)
            .filter(n => n !== null)
            .map(l => ({
              cartLineId: l.id,
              sku: l.sku!, // TODO: Remove `!` once fixed in the schema.
              quantity: l.quantity,
              attributes: l.attributes?.filter(a => a !== null) ?? [],
              discounts: l.discountAllocations.map(e => ({
                amount: e.amount,
                currencyCode: e.currencyCode,
                message: e.title ?? undefined,
              })),
              cost: {
                currencyCode: l.cost.totalAmount.currencyCode,
                totalAmount: l.cost.totalAmount.amount,
                amountPerQuantity: l.cost.amountPerQuantity.amount,
                subtotalAmount: l.cost.subtotalAmount.amount,
                compareAtAmountPerQuantity: l.cost.compareAtAmountPerQuantity?.amount,
              },
            })) ?? [],
        cost: shopifyCart.cost,
        discounts:
          shopifyCart.discountDetails
            ?.filter(e => e !== null)
            .map(e => ({
              amount: e.amount,
              currencyCode: e.currencyCode,
              message: e.title ?? undefined,
            })) ?? [],
        attributes:
          shopifyCart.attributes
            ?.filter(a => a !== null)
            .map(e => ({ key: e.key, value: e.value })) ?? [],
        totalQuantity: shopifyCart.totalQuantity,
      }
    }
    // fallback data if `shopifyCart` isn't present
    return {
      id: null,
      lines: [],
      cost: {
        amount: 0,
        currencyCode: currency,
      },
      discounts: [],
      attributes: [],
      totalQuantity: 0,
    }
  }, [currency, shopifyCart])

  const initializeCart = useCallback(async () => {
    try {
      setStatus('pending')
      const existingCart = cartIdCookie ? await checkoutClient.fetchCart(cartIdCookie) : null
      let cartId: string | undefined = undefined

      // if we found an existing Shopify cart, make it the active cart
      if (existingCart && existingCart.id.length > 0) {
        setShopifyCart(existingCart)
        cartId = existingCart.id
      } else {
        const response = await checkoutClient.createCart({ lines: [] })
        if (response) {
          setShopifyCart(response)
          cartId = response.id
        }
      }
      if (cartId) setActiveCartId(cartId)
      setStatus('resolved')
    } catch {
      // anything we are `await`ing has a chance to fail.
      // if that happens, we should reject so we aren't stuck `pending`
      setStatus('rejected')
    }
  }, [cartIdCookie, checkoutClient])

  useEffect(() => {
    if (!activeCartId) {
      initializeCart()
    }
  }, [activeCartId, initializeCart])

  const addItemsToCart = useCallback(
    async (lines: CartLineInput[]) => {
      setStatus('pending')
      try {
        let response: Omit<FigsCart, 'checkoutUrl'> | null = null
        if (activeCartId) {
          response = await checkoutClient.addLines({
            cartId: activeCartId,
            lines: lines.map(item => ({
              ...item,
              // ensure `quantity` is always at least 1
              quantity: item.quantity || 1,
            })),
          })
        } else {
          response = await checkoutClient.createCart({
            lines,
          })
        }
        if (response) {
          setActiveCartId(response.id)
          setShopifyCart(response)
        }
        setStatus('resolved')
      } catch (error) {
        setStatus('rejected')
        throw error
      }
    },
    [activeCartId, checkoutClient]
  )

  const removeItemsFromCart = useCallback(
    async (lineIdsToRemove: Array<CartLineUpdateInput['id']>) => {
      setStatus('pending')
      try {
        if (activeCartId) {
          const response = await checkoutClient.updateLines({
            cartId: activeCartId,
            lines: lineIdsToRemove.map(lineId => ({
              id: lineId,
              quantity: 0,
            })),
          })
          if (response) setShopifyCart(response)
        }
        setStatus('resolved')
      } catch (error) {
        setStatus('rejected')
        throw error
      }
    },
    [activeCartId, checkoutClient]
  )

  const updateItemsInCart = useCallback(
    async (lines: CartLineUpdateInput[]) => {
      setStatus('pending')
      try {
        if (activeCartId) {
          const response = await checkoutClient.updateLines({
            cartId: activeCartId,
            lines: lines.map(line => ({
              ...line,
            })),
          })
          if (response) setShopifyCart(response)
        }
        setStatus('resolved')
      } catch (error) {
        setStatus('rejected')
        throw error
      }
    },
    [activeCartId, checkoutClient]
  )

  const addCartAttributes = useCallback(
    async (newAttributes: Array<CustomAttributeInput>) => {
      try {
        if (activeCartId) {
          // cart attributes need to be merged since `updateCartAttributes` is always a full-replace
          const mergedAttributes = [...cart.attributes, ...newAttributes]

          const response = await checkoutClient.setCartAttributes({
            cartId: activeCartId,
            attributes: mergedAttributes,
          })
          if (response) setShopifyCart(response)
        }
      } catch {
        // No status to update or error to report as
        // RUM reporting exists in `checkoutClient`.
      }
    },
    [activeCartId, cart.attributes, checkoutClient]
  )

  const retrieveCheckoutUrl = useCallback(
    async (queryParams?: Record<string, string>) => {
      try {
        if (activeCartId) {
          const checkoutUrl = await checkoutClient.fetchCheckoutUrl({
            cartId: activeCartId,
            cartAttributes: cart.attributes,
            queryParams,
          })
          return checkoutUrl
        }
      } catch (error) {
        // No status to update or error to report as
        // RUM reporting exists in `checkoutClient`.
        throw error
      }
      return null
    },
    [activeCartId, cart.attributes, checkoutClient]
  )

  const _testing_internals_syncCartStoreAcrossTabs = useCallback(async () => {
    if (document.visibilityState === 'visible' && activeCartId) {
      const syncedCart = await checkoutClient.fetchCart(activeCartId)
      if (syncedCart && JSON.stringify(syncedCart) !== JSON.stringify(shopifyCart)) {
        setShopifyCart(syncedCart)
      }
    }
  }, [activeCartId, checkoutClient, shopifyCart])

  useEffect(() => {
    document.addEventListener('visibilitychange', _testing_internals_syncCartStoreAcrossTabs)
    return () => {
      document.removeEventListener('visibilitychange', _testing_internals_syncCartStoreAcrossTabs)
    }
  }, [_testing_internals_syncCartStoreAcrossTabs])

  return {
    status,
    cart,
    addItemsToCart,
    removeItemsFromCart,
    updateItemsInCart,
    addCartAttributes,
    retrieveCheckoutUrl,
    _testing_internals_syncCartStoreAcrossTabs,
  }
}
